import {
  CASE_STAGES,
  stageForStatus,
  CLOSED_STAGE_INDEX,
} from '../components/CaseStatusTimeline/CaseStatusTimeline';
import { TicketDto } from '../views/HomeView/HomeView';

/**
 * Lloyd's Jul 8 feedback: the dashboard showed ~50 cases labelled "approving" — legacy
 * (Jimmy-era) wording that doesn't track the 8 stages. Almost all of those are imported Clio
 * cases that never got a TicketStatus row even though their LOR was already sent.
 *
 * This helper derives the correct 8-stage position for a case even when it has no status row,
 * falling back to the LOR flags the importer *did* set. It gives the dashboard a truthful stage
 * label and lets "Cases Needing Action" stop counting already-represented imported cases.
 */

// 0-indexed stages (see CASE_STAGES): 0 Pending payment · 1 LOR sent · 2 LOR confirmed · …
const STAGE_PENDING = 0;
const STAGE_LOR_SENT = 1;
const STAGE_LOR_CONFIRMED = 2;

/**
 * The floor a live court setting puts under a case — "4. Pre-trial scheduled".
 *
 * Twin of COURT_EVENT_FLOOR_STAGE in backend/src/config/caseStages.ts. Change both together.
 */
export const STAGE_PRE_TRIAL = 3;

/**
 * Best-effort stage index for a case. Prefers the real status; when absent, infers from the
 * LOR lifecycle flags (confirmed → sent → pending). Returns -1 only when nothing is known.
 *
 * ## The court-date floor (Lloyd, Aug 31 2026 — Amadu Kamara)
 *
 * A case the court has docketed was still reading "pending", because this function could not see
 * the calendar. `hasFutureCourtEvent` fixes that, and it **raises the stage and never lowers it** —
 * a case already at offer or closed keeps what it has.
 *
 * **Pass a future court EVENT, not `Ticket_CourtDate`.** The latter is frequently just the appear-by
 * date printed on the citation — public intake copies it off the form — so keying this on it would
 * advance unpaid cases out of stage 1 on a date the client typed in. See the backend twin's note.
 *
 * Optional: omit it and the behaviour is exactly what it was before, so screens can be wired up one
 * at a time.
 */
export function deriveStageIndex(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
  hasFutureCourtEvent?: boolean,
): number {
  const base = deriveStageIndexWithoutCourtEvent(ticket, statusName);
  if (!hasFutureCourtEvent) return base;
  return Math.max(base, STAGE_PRE_TRIAL);
}

/** The pre-Sep-1 derivation, kept separate so the floor above is testable in isolation. */
function deriveStageIndexWithoutCourtEvent(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
): number {
  const fromStatus = stageForStatus(statusName);
  if (fromStatus >= 0) return fromStatus;

  if (ticket.Ticket_LorConfirmedAt) return STAGE_LOR_CONFIRMED;
  if (ticket.Ticket_LorSent) return STAGE_LOR_SENT;
  return STAGE_PENDING;
}

/** Human label ("2. LOR sent") for the derived stage, for chips/summaries. */
export function deriveStageLabel(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
  hasFutureCourtEvent?: boolean,
): string {
  const i = deriveStageIndex(ticket, statusName, hasFutureCourtEvent);
  const stage = CASE_STAGES[i];
  return stage ? `${i + 1}. ${stage.short}` : 'Unknown';
}

/**
 * Closed is decided by the status alone, deliberately.
 *
 * The court-event floor can only ever raise a case *to* pre-trial, never to closed, so passing it
 * here could not change the answer — but taking it as a parameter would imply it might. A case is
 * closed because somebody said so.
 */
export function isClosedStage(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
  statusName: string | null | undefined,
): boolean {
  return deriveStageIndex(ticket, statusName) === CLOSED_STAGE_INDEX;
}

/**
 * A case "needs action" when staff must do the next thing. For cases with an explicit status we
 * use the ACTION_STATUSES set (owned by the dashboard). For cases with NO status we only flag the
 * genuinely-new ones — hired but the LOR has not gone out yet. Imported cases whose LOR is already
 * sent are NOT pending; this is what collapses Lloyd's inflated ~50 down to the true handful.
 */
export function noStatusNeedsAction(
  ticket: Pick<TicketDto, 'Ticket_LorSent' | 'Ticket_LorConfirmedAt'>,
): boolean {
  return !ticket.Ticket_LorSent && !ticket.Ticket_LorConfirmedAt;
}
